/**
 * Ask for a reset link.
 *
 * The confirmation is shown whether or not the address exists. Django's
 * ACCOUNT_PREVENT_ENUMERATION is on for the same reason: telling a stranger which addresses have
 * accounts is a disclosure, and there is nothing useful the user could do with the distinction.
 */

import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { auth } from "@/api/auth";
import { Button, Field, Input, Notice } from "@/components/ui/Field";
import { AuthCard } from "@/features/auth/AuthLayout";
import { applyAuthError, type AuthFailure } from "@/features/auth/formErrors";
import { fieldMessage } from "@/lib/fieldMessage";
import { forgotPasswordSchema, type ForgotPasswordValues } from "@/features/auth/schemas";

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [sent, setSent] = useState(false);
  const [failure, setFailure] = useState<AuthFailure>({});

  const form = useForm<ForgotPasswordValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: "" },
  });

  const submit = form.handleSubmit(async (values) => {
    setFailure({});
    try {
      await auth.requestPasswordReset(values.email);
      setSent(true);
    } catch (error) {
      const applied = applyAuthError(error, {
        setError: form.setError,
        fields: ["email"],
        fallback: t("auth.errors.unexpected"),
      });
      // allauth answers 401 here when nobody is signed in, which is the normal case.
      if (applied.formError || Object.keys(form.formState.errors).length > 0) setFailure(applied);
      else setSent(true);
    }
  });

  if (sent) {
    return (
      <AuthCard title={t("auth.forgot.sentTitle")} description={t("auth.forgot.sentBody")}>
        <Link to="/auth/sign-in" className="text-brand-700 block text-center text-sm underline">
          {t("auth.backToSignIn")}
        </Link>
      </AuthCard>
    );
  }

  return (
    <AuthCard title={t("auth.forgot.title")} description={t("auth.forgot.subtitle")}>
      <form onSubmit={submit} noValidate className="space-y-4">
        <Field
          name="email"
          label={t("auth.email")}
          error={fieldMessage(form.formState.errors, "email", t)}
        >
          <Input
            id="email"
            type="email"
            autoComplete="email"
            inputMode="email"
            autoCapitalize="none"
            invalid={!!form.formState.errors.email}
            {...form.register("email")}
          />
        </Field>
        {failure.formError && <Notice tone="error">{failure.formError}</Notice>}
        <Button type="submit" disabled={form.formState.isSubmitting}>
          {form.formState.isSubmitting ? t("auth.forgot.working") : t("auth.forgot.submit")}
        </Button>
      </form>
      <Link to="/auth/sign-in" className="text-brand-700 block text-center text-sm underline">
        {t("auth.backToSignIn")}
      </Link>
    </AuthCard>
  );
}
